import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { ethers } from "ethers";
import { z } from "zod";

// Schema for getting ERC20 balance parameters
export const GetPrivateERC20BalanceSchema = z.object({
    tokenAddress: z.string().describe("ERC20 token contract address"),
    accountAddress: z.string().optional().describe("Account address to check balance for"),
});

export type GetPrivateERC20BalanceParams = z.infer<typeof GetPrivateERC20BalanceSchema>;

// Basic ERC20 ABI for balance checking
const ERC20_ABI = [
    "function balanceOf(address account) view returns (uint256)",
    "function name() view returns (string)",
    "function symbol() view returns (string)",
    "function decimals() view returns (uint8)",
    "function totalSupply() view returns (uint256)"
];

export const getPrivateErc20BalanceAction: Action = {
    name: "GET_PRIVATE_ERC20_BALANCE",
    similes: [
        "CHECK_ERC20_BALANCE",
        "GET_TOKEN_BALANCE",
        "CHECK_TOKEN_BALANCE",
        "ERC20_BALANCE",
        "TOKEN_BALANCE",
        "BALANCE_ERC20"
    ],
    description: "Get the balance of a private ERC20 token for a specific address on the COTI blockchain.",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            const text = message.content?.text || "";
            elizaLogger.info(`GET_PRIVATE_ERC20_BALANCE validation - text: "${text}"`);
            
            // Check for balance keywords with token/ERC20
            const isTokenBalanceQuery = /\b(balance|check.*balance|get.*balance).*\b(token|erc20)\b/i.test(text) ||
                                       /\b(token|erc20).*\b(balance|check.*balance|get.*balance)\b/i.test(text);
            elizaLogger.info(`GET_PRIVATE_ERC20_BALANCE validation - isTokenBalanceQuery: ${isTokenBalanceQuery}`);
            
            // Check if message content has required fields
            if (message.content?.tokenAddress) {
                elizaLogger.info(`GET_PRIVATE_ERC20_BALANCE validation - structured params found`);
                return true;
            }

            // Check if text contains at least one address and mentions token balance
            const addressMatches = text.match(/0x[a-fA-F0-9]{40}/g);
            elizaLogger.info(`GET_PRIVATE_ERC20_BALANCE validation - addresses found: ${addressMatches?.length || 0}`);

            if (isTokenBalanceQuery && addressMatches && addressMatches.length >= 1) {
                elizaLogger.info(`GET_PRIVATE_ERC20_BALANCE validation - returning true`);
                return true;
            }

            elizaLogger.info(`GET_PRIVATE_ERC20_BALANCE validation - returning false`);
            return false;
        } catch (error) {
            elizaLogger.error(`GET_PRIVATE_ERC20_BALANCE validation error:`, error);
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting GET_PRIVATE_ERC20_BALANCE action");

            let params: GetPrivateERC20BalanceParams;

            // Try to get parameters from message content
            if (message.content?.tokenAddress) {
                params = GetPrivateERC20BalanceSchema.parse(message.content);
            } else {
                // Extract from message text
                const text = message.content?.text || "";
                const addressMatches = text.match(/0x[a-fA-F0-9]{40}/g);
                
                if (!addressMatches || addressMatches.length < 1) {
                    throw new Error("Could not extract token contract address from message");
                }
                
                // First address is typically the token contract
                // Second address (if present) is the account to check
                params = {
                    tokenAddress: addressMatches[0],
                    accountAddress: addressMatches.length > 1 ? addressMatches[1] : undefined
                };
            }

            const walletProvider = initCotiWalletProvider(runtime);
            const provider = walletProvider.getProvider();
            
            // Use provided account address or wallet's own address
            const accountAddress = params.accountAddress || walletProvider.getAddress();

            // Create contract instance
            const contract = new ethers.Contract(
                params.tokenAddress,
                ERC20_ABI,
                provider
            );

            elizaLogger.info(`Getting ERC20 balance for account ${accountAddress} from token ${params.tokenAddress}`);

            // Get token info
            const [balance, name, symbol, decimals] = await Promise.all([
                contract.balanceOf(accountAddress),
                contract.name().catch(() => "Unknown Token"),
                contract.symbol().catch(() => "UNKNOWN"),
                contract.decimals().catch(() => 18)
            ]);

            // Format balance
            const formattedBalance = ethers.formatUnits(balance, decimals);

            const responseText = `ERC20 Token Balance:
Token: ${name} (${symbol})
Contract: ${params.tokenAddress}
Account: ${accountAddress}
Balance: ${formattedBalance} ${symbol}
Decimals: ${decimals}`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        tokenAddress: params.tokenAddress,
                        accountAddress,
                        balance: formattedBalance,
                        balanceWei: balance.toString(),
                        tokenName: name,
                        tokenSymbol: symbol,
                        decimals: decimals.toString(),
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    tokenAddress: params.tokenAddress,
                    accountAddress,
                    balance: formattedBalance,
                    balanceWei: balance.toString(),
                    tokenName: name,
                    tokenSymbol: symbol,
                    decimals: decimals.toString(),
                },
            };
        } catch (error) {
            elizaLogger.error("Error in GET_PRIVATE_ERC20_BALANCE action:", error);
            const errorMessage = `Failed to get ERC20 token balance: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Check my token balance for contract 0x1234567890123456789012345678901234567890",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll check your ERC20 token balance for that contract.",
                    action: "GET_PRIVATE_ERC20_BALANCE",
                },
            },
        ],
    ],
};
